/**
 * Quotable statistics and FAQ pairs derived from a topic index.
 *
 * Answer engines cite a number attached to a named source. These helpers turn
 * the ranked table into standalone numeric claims and question/answer pairs,
 * and they are the single source used by BOTH the visible page content and the
 * FAQPage JSON-LD, so the two can never drift.
 */
import type { TopicDef } from "@/lib/topics-config";
import type { TopicIndexData } from "@/lib/topics.server";

export interface KeyStat {
  label: string;
  value: string;
  detail: string;
}

function fmt(value: number | null | undefined, decimals: number) {
  if (value == null) return "—";
  return value.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

/** Standalone numeric claims an answer engine can lift verbatim. */
export function topicKeyStats(def: TopicDef, data: TopicIndexData): KeyStat[] {
  const entries = data.entries;
  if (entries.length === 0) return [];
  const top = entries[0]!;
  const bottom = entries[entries.length - 1]!;
  const mid = median(entries.map((e) => e.value));
  const stats: KeyStat[] = [
    {
      label: `Highest ${def.metricLabel.toLowerCase()}`,
      value: `${fmt(top.value, def.decimals)} ${def.unit}`,
      detail: `${top.makeName} ${top.modelName} (${top.year})`,
    },
  ];
  if (mid != null) {
    stats.push({
      label: "Median across the field",
      value: `${fmt(mid, def.decimals)} ${def.unit}`,
      detail: `Midpoint of ${data.modelCount} ranked models`,
    });
  }
  if (entries.length > 1) {
    stats.push({
      label: "Spread, first to last",
      value: `${fmt(top.value - bottom.value, def.decimals)} ${def.unit}`,
      detail: `${fmt(bottom.value, def.decimals)} ${def.unit} at the bottom of the table`,
    });
  }
  stats.push({
    label: "Models compared",
    value: data.modelCount.toLocaleString("en-US"),
    detail: `Data current through model year ${data.latestYear}`,
  });
  return stats;
}

/** One sentence per key stat, each carrying its own attribution. */
export function topicStatSentences(def: TopicDef, data: TopicIndexData): string[] {
  return topicKeyStats(def, data).map(
    (s) => `${s.label}: ${s.value} — ${s.detail}. Source: AutoSpecData analysis, ${data.latestYear}.`,
  );
}

/**
 * FAQ pairs shown on the page and emitted as FAQPage JSON-LD. Every answer is
 * built from real rows; when the table is empty the caller renders nothing.
 */
export function topicFaqs(def: TopicDef, data: TopicIndexData, answer: string) {
  const entries = data.entries;
  if (entries.length === 0) return [];
  const top = entries[0]!;
  const runnerUp = entries[1];
  const bottom = entries[entries.length - 1]!;
  const metric = def.metricLabel.toLowerCase();

  const items = [
    {
      q: `Which vehicle has the highest ${metric}?`,
      a: answer,
    },
    {
      q: `What is the ${metric} of the ${top.makeName} ${top.modelName}?`,
      a: `The ${top.year} ${top.makeName} ${top.modelName} is rated at ${fmt(top.value, def.decimals)} ${def.unitLong}${
        top.config ? ` (${top.config})` : ""
      }, the highest figure in this chart of ${data.modelCount} models.`,
    },
  ];

  if (runnerUp) {
    items.push({
      q: `How does the ${runnerUp.makeName} ${runnerUp.modelName} compare?`,
      a: `The ${runnerUp.year} ${runnerUp.makeName} ${runnerUp.modelName} ranks second at ${fmt(
        runnerUp.value,
        def.decimals,
      )} ${def.unitLong}, ${fmt(top.value - runnerUp.value, def.decimals)} ${def.unit} behind the ${top.makeName} ${top.modelName}.`,
    });
  }

  items.push(
    {
      q: `What is a typical ${metric} figure?`,
      a: `Across the ${data.modelCount} models in this chart the range runs from ${fmt(
        bottom.value,
        def.decimals,
      )} to ${fmt(top.value, def.decimals)} ${def.unit}, so anything near the middle of that band is ordinary for the segment.`,
    },
    {
      q: "Where does this data come from?",
      a: def.intro,
    },
    {
      q: "What should I know before comparing these numbers?",
      a: def.notes,
    },
  );

  return items;
}
